import React from "react";
import { ChevronLeft } from "lucide-react";

import Button from "@/components/ui/Button.jsx";
import Card from "@/components/ui/Card.jsx";
import InputOtp from "@/components/ui/InputOtp.jsx";
import SaveChip from "@/sot/primitives/SaveChip.jsx";
import Snackbar from "@/sot/primitives/Snackbar.jsx";
import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";
import supabase from "@/services/supabaseClient";
import { prettyFormat, splitE164ToCcLocal } from "@/utils/phone.js";

const ICON_SMS = "/icons/list-items/chat.svg";
const ICON_GOOGLE = "/icons/appointments/Google-Authenticator.svg";
const FLOW_STATE_KEY_PREFIX = "afrimed:2fa:setup-flow:v1";
const SECURITY_FIELDS =
  "user_id,sms_2fa_enabled,sms_phone_cc,sms_phone_local,sms_last_confirmed_at,auth_app_2fa_enabled,auth_app_label,auth_app_last_confirmed_at,updated_at";

function flowStateKey(scope = "default") {
  return `${FLOW_STATE_KEY_PREFIX}:${scope}`;
}

function readFlowState(scope) {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(flowStateKey(scope));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeFlowState(scope, value) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(flowStateKey(scope), JSON.stringify(value));
  } catch {}
}

function clearFlowState(scope) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(flowStateKey(scope));
  } catch {}
}

function nextPaint() {
  return new Promise((resolve) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(resolve);
    });
  });
}

function normalizePhone(value) {
  const parts = splitE164ToCcLocal(value);
  return parts?.e164 || "";
}

function readVerifiedPhone({ user, profile, profileRow }) {
  const profilePhone = normalizePhone(
    profileRow?.phone || profileRow?.phone_number || profile?.phone || profile?.phone_number || ""
  );
  const authPhone = normalizePhone(user?.phone || "");
  const profileVerifiedAt =
    profileRow?.phone_verified_at ||
    profile?.phone_verified_at ||
    "";
  const authPhoneConfirmedAt =
    user?.phone_confirmed_at ||
    user?.phoneConfirmedAt ||
    "";

  if (profilePhone && profileVerifiedAt) return profilePhone;
  if (authPhone && authPhoneConfirmedAt && (!profilePhone || profilePhone === authPhone)) {
    return authPhone;
  }
  return "";
}

async function loadRoleProfilePhone(userId, role) {
  const table = role === "doctor" ? "doctor_profiles" : "patient_profiles";
  const { data, error } = await supabase
    .from(table)
    .select("phone,phone_verified_at")
    .eq("user_id", userId)
    .maybeSingle();
  if (error) throw error;
  return data || null;
}

async function sendSmsOtp(phone) {
  const { data, error } = await supabase.functions.invoke("send-otp", {
    body: { phone },
  });
  if (error) throw error;
  if (data?.ok === false) {
    const err = new Error(data?.error || "sms_send_failed");
    err.code = data?.error || "sms_send_failed";
    throw err;
  }
  return data || { ok: true };
}

async function verifySmsOtp(phone, code) {
  const { data, error } = await supabase.functions.invoke("verify-otp", {
    body: { phone, code },
  });
  if (error) throw error;
  if (data?.ok !== true) {
    const err = new Error(data?.error || "sms_verify_failed");
    err.code = data?.error || "sms_verify_failed";
    throw err;
  }
  return data;
}

async function saveSmsEnabled(userId, phone) {
  const parts = splitE164ToCcLocal(phone);
  if (!parts?.cc || !parts?.local) {
    const err = new Error("invalid_verified_phone");
    err.code = "invalid_verified_phone";
    throw err;
  }

  const payload = {
    sms_2fa_enabled: true,
    sms_phone_cc: parts.cc,
    sms_phone_local: parts.local,
    sms_last_confirmed_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  const update = await supabase
    .from("user_security_settings")
    .update(payload)
    .eq("user_id", userId)
    .select(SECURITY_FIELDS)
    .maybeSingle();

  if (!update.error && update.data?.user_id) return update.data;

  const insert = await supabase
    .from("user_security_settings")
    .insert({ user_id: userId, ...payload })
    .select(SECURITY_FIELDS)
    .maybeSingle();

  if (insert.error) throw insert.error;
  if (!insert.data?.user_id) {
    const err = new Error("security_settings_save_failed");
    err.code = "security_settings_save_failed";
    throw err;
  }
  return insert.data;
}

async function saveAuthAppEnabled(userId, label) {
  const payload = {
    auth_app_2fa_enabled: true,
    auth_app_label: String(label || "Authenticator app").trim() || "Authenticator app",
    auth_app_last_confirmed_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  };

  const update = await supabase
    .from("user_security_settings")
    .update(payload)
    .eq("user_id", userId)
    .select(SECURITY_FIELDS)
    .maybeSingle();

  if (!update.error && update.data?.user_id) return update.data;

  const insert = await supabase
    .from("user_security_settings")
    .insert({ user_id: userId, ...payload })
    .select(SECURITY_FIELDS)
    .maybeSingle();

  if (insert.error) throw insert.error;
  if (!insert.data?.user_id) {
    const err = new Error("security_settings_save_failed");
    err.code = "security_settings_save_failed";
    throw err;
  }
  return insert.data;
}

function SegmentedProgress({ step = 1 }) {
  return (
    <div className="mt-3 flex items-center gap-2">
      {[1, 2, 3].map((segment) => (
        <div
          key={segment}
          className={[
            "h-1 flex-1 rounded-full transition-colors",
            segment <= step
              ? "bg-[rgb(var(--afrimed-primary))]"
              : "bg-[rgba(29,79,215,0.16)]",
          ].join(" ")}
        />
      ))}
    </div>
  );
}

function FlowHeader({ step, onBack, backLabel }) {
  const safeTop = "calc(var(--afrimed-head-air, 28px) + env(safe-area-inset-top))";

  return (
    <header className="w-full">
      <div style={{ height: safeTop }} />
      <div className="flex items-center justify-between">
        <button
          type="button"
          aria-label={backLabel}
          onClick={onBack}
          className="topbar-back inline-flex h-10 w-10 items-center justify-center text-[rgb(var(--clr-on-surface,4,8,18))]"
        >
          <ChevronLeft className="h-5 w-5" aria-hidden />
        </button>
        <div className="min-w-[56px] text-right text-body-medium text-on-surface-var">
          {step}/3
        </div>
      </div>
      <SegmentedProgress step={step} />
    </header>
  );
}

function formatTotpSecret(value) {
  const chunks = String(value || "")
    .replace(/\s+/g, "")
    .toUpperCase()
    .match(/.{1,4}/g);
  return chunks?.join(" ") || "";
}

function normalizeTotpSecret(value) {
  return String(value || "").replace(/\s+/g, "").toUpperCase();
}

function formatVisibleIssuer(value) {
  const issuer = String(value || "").trim();
  return issuer === "AfriMed" ? "AfriMed™" : issuer;
}

function parseTotpUri(uri, fallbackSecret = "", fallbackAccount = "") {
  const fallback = {
    issuer: "AfriMed",
    account: String(fallbackAccount || "").trim(),
    secret: normalizeTotpSecret(fallbackSecret),
    algorithm: "SHA1",
    digits: "6",
    period: "30",
    secretMatchesEnrollment: false,
  };

  try {
    const parsed = new URL(String(uri || ""));
    const label = decodeURIComponent(parsed.pathname.replace(/^\/+/, ""));
    const issuer = parsed.searchParams.get("issuer") || label.split(":")[0] || fallback.issuer;
    const account = label.includes(":")
      ? label.slice(label.indexOf(":") + 1)
      : label || fallback.account;
    const secret = normalizeTotpSecret(parsed.searchParams.get("secret") || fallback.secret);

    return {
      issuer: issuer || fallback.issuer,
      account: account || fallback.account,
      secret,
      algorithm: (parsed.searchParams.get("algorithm") || fallback.algorithm).toUpperCase(),
      digits: parsed.searchParams.get("digits") || fallback.digits,
      period: parsed.searchParams.get("period") || fallback.period,
      secretMatchesEnrollment: secret === fallback.secret,
    };
  } catch {
    return {
      ...fallback,
      secretMatchesEnrollment: Boolean(fallback.secret),
    };
  }
}

function buildQrDataUrl(qrCode) {
  const raw = String(qrCode || "").trim();
  if (!raw) return "";
  if (/^data:image\//i.test(raw)) return raw;
  return `data:image/svg+xml;utf-8,${encodeURIComponent(raw)}`;
}

function readSupabaseErrorCode(error) {
  return String(error?.code || error?.error_code || error?.name || "").trim().toLowerCase();
}

function isFactorNameConflict(error) {
  const code = readSupabaseErrorCode(error);
  const message = String(error?.message || "").trim().toLowerCase();
  return (
    code === "mfa_factor_name_conflict" ||
    message.includes("mfa_factor_name_conflict") ||
    message.includes("factor name") ||
    message.includes("friendly name")
  );
}

function isTotpVerificationError(error) {
  const code = readSupabaseErrorCode(error);
  const message = String(error?.message || "").trim().toLowerCase();
  return (
    code.includes("mfa") ||
    message.includes("factor") ||
    message.includes("challenge") ||
    message.includes("verification") ||
    message.includes("totp") ||
    message.includes("code")
  );
}

function authAppActivationErrorMessage(t, error) {
  const code = readSupabaseErrorCode(error);
  const message = String(error?.message || "").trim();
  const normalizedMessage = message.toLowerCase();

  if (code === "mfa_verification_failed" || normalizedMessage.includes("verification failed")) {
    return t("settings.twoFactor.errors.verifyCode", "We couldn't verify that code.");
  }
  if (code === "mfa_challenge_expired" || normalizedMessage.includes("challenge expired")) {
    return t(
      "settings.twoFactor.errors.authAppChallengeExpired",
      "That verification challenge expired. Enter the latest 6-digit code and try again."
    );
  }
  if (code === "mfa_factor_not_found" || normalizedMessage.includes("factor not found")) {
    return t(
      "settings.twoFactor.errors.authAppFactorMissing",
      "This authenticator setup is no longer available. Start over to create a new setup key."
    );
  }
  if (code === "missing_auth_app_enrollment" || code === "missing_auth_app_factor") {
    return t(
      "settings.twoFactor.errors.authAppFactorMissing",
      "This authenticator setup is no longer available. Start over to create a new setup key."
    );
  }
  if (code === "security_settings_save_failed" || normalizedMessage.includes("security_settings")) {
    return t(
      "settings.twoFactor.errors.authAppSave",
      "Authenticator verified, but we couldn't update your security settings. Please try again."
    );
  }
  if (isTotpVerificationError(error)) {
    return message || t("settings.twoFactor.errors.verifyCode", "We couldn't verify that code.");
  }
  return t(
    "settings.twoFactor.errors.authAppVerifyUnexpected",
    "We couldn't finish authenticator verification. Please try again."
  );
}

function listTotpFactorsFromResponse(response) {
  return (Array.isArray(response?.data?.all) ? response.data.all : []).filter(
    (factor) => factor?.factor_type === "totp"
  );
}

function MethodCard({ icon, label, value, active = false, disabled = false, onClick }) {
  return (
    <Card
      as="button"
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={[
        "w-full px-3 py-3 text-left transition",
        active ? "ring-2 ring-[rgba(29,79,215,0.24)]" : "",
        disabled ? "opacity-50" : "hover:bg-[rgb(var(--clr-surface-cont-highest))]",
      ].join(" ")}
    >
      <div className="flex items-center gap-3">
        <img src={icon} alt="" className="h-6 w-6 shrink-0" draggable="false" />
        <div className="min-w-0 flex-1">
          <div className="text-label-medium text-on-surface-var">{label}</div>
          <div className="truncate text-body-large text-on-surface">{value}</div>
        </div>
        <div className="text-body-large text-on-surface-var">›</div>
      </div>
    </Card>
  );
}

function SuccessBadge() {
  return (
    <div className="relative flex justify-center">
      <img
        src="/icons/You're in.svg"
        alt=""
        className="h-[138px] w-[138px] md:h-[204px] md:w-[204px]"
        draggable="false"
      />
      <div className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
        <svg
          viewBox="0 0 25 25"
          className="h-[44px] w-[44px] overflow-visible md:h-[68px] md:w-[68px]"
        >
          <path
            d="M3.44 13.527 L9.078 19.165 L21.182 7.061"
            fill="none"
            stroke="#FFFFFF"
            strokeWidth="2.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            pathLength="100"
            className="animate-afrimed-check"
          />
        </svg>
      </div>
    </div>
  );
}

function CircularProgressIndicator({ progress = 80, label = "Loading" }) {
  const size = 48;
  const radius = 20;
  const circumference = 2 * Math.PI * radius;
  const resolvedProgress = Math.max(0, Math.min(100, Number(progress) || 0));
  const dashoffset = circumference - (resolvedProgress / 100) * circumference;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(resolvedProgress)}
    >
      <circle
        cx="24"
        cy="24"
        r={radius}
        stroke="rgb(var(--afrimed-primary))"
        strokeOpacity="0.16"
        strokeWidth="4"
        fill="none"
      />
      <circle
        cx="24"
        cy="24"
        r={radius}
        stroke="rgb(var(--afrimed-primary))"
        strokeWidth="4"
        strokeLinecap="round"
        fill="none"
        transform="rotate(-90 24 24)"
        strokeDasharray={`${circumference} ${circumference}`}
        strokeDashoffset={dashoffset}
        style={{ transition: "stroke-dashoffset 220ms ease" }}
      />
    </svg>
  );
}

function FlowScreen({ children, footer = null, contentClassName = "", footerClassName = "" }) {
  return (
    <div className="mx-auto flex min-h-[calc(100dvh-248px)] w-full max-w-[520px] flex-col md:min-h-[calc(100dvh-316px)]">
      <div
        className={[
          "flex flex-1 items-start justify-center pt-6 md:pt-8",
          contentClassName,
        ].join(" ")}
      >
        <div className="w-full">{children}</div>
      </div>
      {footer ? (
        <div
          className={[
            "w-full pt-6 pb-[calc(72px+env(safe-area-inset-bottom))] md:pb-2",
            footerClassName,
          ].join(" ")}
        >
          {footer}
        </div>
      ) : null}
    </div>
  );
}

export default function TwoFactorSetupFlow({
  defaultMethod = "sms",
  role = "patient",
  authAppAvailable = true,
  onExit,
  onDone,
}) {
  const { t } = useLanguage();
  const flowScope = `role:${role}:method:${defaultMethod}`;
  const storedFlow = React.useMemo(() => readFlowState(flowScope), [flowScope]);
  const auth = (typeof useAuth === "function" ? useAuth() : {}) || {};
  const { user, profile } = auth;

  const [step, setStep] = React.useState(() => Number(storedFlow?.step || 1) || 1);
  const [selectedMethod, setSelectedMethod] = React.useState(
    () => storedFlow?.selectedMethod || defaultMethod
  );
  const [verifiedPhone, setVerifiedPhone] = React.useState("");
  const [loadingPhone, setLoadingPhone] = React.useState(true);
  const [sent, setSent] = React.useState(() => storedFlow?.sent === true);
  const [code, setCode] = React.useState("");
  const [countdown, setCountdown] = React.useState(20);
  const [sending, setSending] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");
  const [snackbar, setSnackbar] = React.useState("");
  const [showManualKey, setShowManualKey] = React.useState(false);
  const [showCompletionLoader, setShowCompletionLoader] = React.useState(false);
  const [completionProgress, setCompletionProgress] = React.useState(10);
  const [authAppEnrollment, setAuthAppEnrollment] = React.useState(null);
  const [authAppLoading, setAuthAppLoading] = React.useState(false);
  const [unfinishedAuthAppFactorId, setUnfinishedAuthAppFactorId] = React.useState("");

  const phoneDisplay = verifiedPhone
    ? prettyFormat(verifiedPhone)
    : t("settings.twoFactor.flow.noVerifiedPhone", "No verified phone on file");
  const smsAvailable = !!verifiedPhone;
  const selectedMethodAvailable = selectedMethod === "sms" ? smsAvailable : authAppAvailable;
  const manualKey = React.useMemo(
    () => formatTotpSecret(authAppEnrollment?.secret),
    [authAppEnrollment?.secret]
  );
  const rawManualKey = React.useMemo(
    () => normalizeTotpSecret(authAppEnrollment?.secret),
    [authAppEnrollment?.secret]
  );
  const manualSetupDetails = React.useMemo(
    () => parseTotpUri(authAppEnrollment?.uri, authAppEnrollment?.secret, user?.email),
    [authAppEnrollment?.secret, authAppEnrollment?.uri, user?.email]
  );
  const authAppQrSrc = React.useMemo(
    () => buildQrDataUrl(authAppEnrollment?.qrCode),
    [authAppEnrollment?.qrCode]
  );
  const authAppLabel = React.useMemo(
    () => t("settings.twoFactor.flow.authAppFactorLabel", "AfriMed™ authenticator"),
    [t]
  );

  React.useEffect(() => {
    let mounted = true;

    async function loadPhone() {
      setLoadingPhone(true);
      setError("");
      try {
        if (!user?.id) {
          if (mounted) setVerifiedPhone("");
          return;
        }
        const profileRow = await loadRoleProfilePhone(user.id, role);
        if (!mounted) return;
        setVerifiedPhone(readVerifiedPhone({ user, profile, profileRow }));
      } catch {
        if (!mounted) return;
        setVerifiedPhone("");
        setError(
          t(
            "settings.twoFactor.errors.loadPhone",
            "Could not load your verified phone."
          )
        );
      } finally {
        if (mounted) setLoadingPhone(false);
      }
    }

    loadPhone();
    return () => {
      mounted = false;
    };
  }, [profile, role, t, user]);

  React.useEffect(() => {
    if (step !== 2 || selectedMethod !== "sms" || countdown <= 0) return undefined;
    const timer = window.setTimeout(() => {
      setCountdown((current) => Math.max(0, current - 1));
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [countdown, selectedMethod, step]);

  React.useEffect(() => {
    if (!storedFlow?.selectedMethod) {
      setSelectedMethod(defaultMethod);
    }
  }, [defaultMethod, storedFlow?.selectedMethod]);

  React.useEffect(() => {
    writeFlowState(flowScope, { step, selectedMethod, sent });
  }, [flowScope, selectedMethod, sent, step]);

  React.useEffect(() => {
    if (!showCompletionLoader) {
      setCompletionProgress(10);
      return undefined;
    }

    const frames = [10, 30, 50, 80];
    let index = 0;
    const timer = window.setInterval(() => {
      index += 1;
      setCompletionProgress(frames[Math.min(index, frames.length - 1)]);
    }, 180);

    return () => window.clearInterval(timer);
  }, [showCompletionLoader]);

  React.useEffect(() => {
    if (step !== 2 || selectedMethod !== "auth-app" || authAppEnrollment?.factorId) return;
    void startAuthAppEnrollment();
  }, [authAppEnrollment?.factorId, selectedMethod, step]);

  function openSnack(message) {
    setSnackbar(message);
    window.clearTimeout(openSnack._timer);
    openSnack._timer = window.setTimeout(() => setSnackbar(""), 4000);
  }

  function handleBack() {
    if (sending || submitting || showCompletionLoader) return;
    if (step === 1) {
      clearFlowState(flowScope);
      onExit?.();
      return;
    }
    if (step === 2) {
      setError("");
      setCode("");
      setShowManualKey(false);
      setCountdown(20);
      setSent(false);
      setStep(1);
      return;
    }
    if (step === 3) {
      setError("");
      setShowManualKey(false);
      writeFlowState(flowScope, { step: 2, selectedMethod, sent });
      setStep(2);
    }
  }

  async function handleSendSmsCode() {
    if (!verifiedPhone || sending) return false;
    setSending(true);
    setError("");
    try {
      await sendSmsOtp(verifiedPhone);
      setSent(true);
      setCode("");
      setCountdown(20);
      return true;
    } catch {
      setError(
        t(
          "settings.twoFactor.errors.sendCode",
          "Could not send the SMS code."
        )
      );
      return false;
    } finally {
      setSending(false);
    }
  }

  async function startAuthAppEnrollment({
    forceFresh = false,
    discardUnverified = false,
  } = {}) {
    if ((!forceFresh && authAppEnrollment?.factorId) || authAppLoading) return true;
    setAuthAppLoading(true);
    setError("");
    try {
      const factorList = await supabase.auth.mfa.listFactors();
      if (factorList.error) throw factorList.error;

      const existingTotpFactors = listTotpFactorsFromResponse(factorList);
      const verifiedTotpFactor = existingTotpFactors.find((factor) => factor?.status === "verified");
      if (verifiedTotpFactor?.id) {
        setError(
          t(
            "settings.twoFactor.errors.authAppAlreadyEnabled",
            "Authenticator app is already enabled for this account."
          )
        );
        return false;
      }

      const unverifiedTotpFactors = existingTotpFactors.filter(
        (factor) => factor?.id && factor?.status === "unverified"
      );
      if (unverifiedTotpFactors.length > 0) {
        setUnfinishedAuthAppFactorId(unverifiedTotpFactors[0].id);
        if (!discardUnverified) {
          setError(
            t(
              "settings.twoFactor.errors.authAppUnfinishedSetup",
              "An authenticator setup is already in progress. You can start over to create a new setup key."
            )
          );
          return false;
        }

        const cleanup = await supabase.functions.invoke("reset-unverified-totp-factor", {
          body: {
            factorId: unverifiedTotpFactors[0].id,
          },
        });
        if (cleanup.error) throw cleanup.error;
        if (cleanup.data?.ok !== true) {
          const cleanupError = new Error(cleanup.data?.error || "auth_app_reset_failed");
          cleanupError.code = cleanup.data?.error || "auth_app_reset_failed";
          throw cleanupError;
        }
        setUnfinishedAuthAppFactorId("");
      }

      const { data, error: enrollError } = await supabase.auth.mfa.enroll({
        factorType: "totp",
        issuer: "AfriMed",
        friendlyName: authAppLabel,
      });
      if (enrollError) {
        if (isFactorNameConflict(enrollError)) {
          setError(
            t(
              "settings.twoFactor.errors.authAppConflict",
              "An unfinished authenticator setup is still clearing. Please try again."
            )
          );
          return false;
        }
        throw enrollError;
      }

      const secret = data?.totp?.secret || "";
      const qrCode = data?.totp?.qr_code || "";
      const uri = data?.totp?.uri || "";
      if (!data?.id || !secret || !qrCode) {
        throw new Error("mfa_enrollment_missing_totp");
      }

      setAuthAppEnrollment({
        factorId: data.id,
        label: data?.friendly_name || authAppLabel,
        secret,
        qrCode,
        uri,
      });
      setCode("");
      setShowManualKey(false);
      setUnfinishedAuthAppFactorId("");
      return true;
    } catch (enrollError) {
      if (discardUnverified) {
        setError(
          t(
            "settings.twoFactor.errors.authAppResetFailed",
            "We couldn't reset the unfinished authenticator setup. Please contact support if you need it reset."
          )
        );
        return false;
      }
      if (isFactorNameConflict(enrollError)) {
        setError(
          t(
            "settings.twoFactor.errors.authAppConflict",
            "An unfinished authenticator setup is still clearing. Please try again."
          )
        );
        return false;
      }
      setError(
        t(
          "settings.twoFactor.errors.authAppEnroll",
          "Could not start authenticator app setup."
        )
      );
      return false;
    } finally {
      setAuthAppLoading(false);
    }
  }

  async function handleRegisterNewDevice() {
    if (authAppLoading || submitting || showCompletionLoader) return;
    setSelectedMethod("auth-app");
    setCode("");
    setShowManualKey(false);
    setSent(false);
    setCountdown(20);
    if (authAppEnrollment?.factorId) {
      setStep(2);
      setError(
        t(
          "settings.twoFactor.errors.authAppFinishCurrentSetup",
          "Finish this authenticator setup first. We won't remove it automatically."
        )
      );
      return;
    }
    const ok = await startAuthAppEnrollment({ forceFresh: true });
    if (ok) {
      writeFlowState(flowScope, { step: 2, selectedMethod: "auth-app", sent: false });
      setStep(2);
    }
  }

  async function handleStartOverAuthAppSetup() {
    if (authAppLoading || submitting || showCompletionLoader) return;
    setSelectedMethod("auth-app");
    setCode("");
    setShowManualKey(false);
    setSent(false);
    setCountdown(20);
    setAuthAppEnrollment(null);
    const ok = await startAuthAppEnrollment({
      forceFresh: true,
      discardUnverified: true,
    });
    if (ok) {
      writeFlowState(flowScope, { step: 2, selectedMethod: "auth-app", sent: false });
      setStep(2);
      return;
    }
  }

  async function handleContinueStepOne() {
    if (loadingPhone || !selectedMethodAvailable) return;
    setError("");
    if (selectedMethod === "sms") {
      const ok = sent || (await handleSendSmsCode());
      if (!ok) return;
    } else if (selectedMethod === "auth-app") {
      const ok = await startAuthAppEnrollment();
      if (!ok) return;
    }
    setStep(2);
    setCountdown(20);
  }

  async function activateCurrentMethod() {
    setSubmitting(true);
    setError("");
    setShowCompletionLoader(true);
    writeFlowState(flowScope, { step: 2, selectedMethod, sent });

    try {
      await nextPaint();
      if (selectedMethod === "sms") {
        if (!user?.id || !verifiedPhone) throw new Error("missing_verified_phone");
        await verifySmsOtp(verifiedPhone, code);
        await saveSmsEnabled(user.id, verifiedPhone);
      } else {
        if (!user?.id || !authAppEnrollment?.factorId) {
          throw new Error("missing_auth_app_enrollment");
        }
        const factorId = authAppEnrollment.factorId;
        const factorList = await supabase.auth.mfa.listFactors();
        if (factorList.error) throw factorList.error;
        const currentFactor = listTotpFactorsFromResponse(factorList).find(
          (factor) => factor?.id === factorId
        );
        if (!currentFactor?.id) throw new Error("missing_auth_app_factor");
        if (currentFactor.status === "verified") {
          await saveAuthAppEnabled(user.id, authAppEnrollment.label || authAppLabel);
          await new Promise((resolve) => window.setTimeout(resolve, 700));
          setShowCompletionLoader(false);
          setStep(3);
          return;
        }
        if (currentFactor.status !== "unverified") throw new Error("invalid_auth_app_factor_state");

        const challenge = await supabase.auth.mfa.challenge({
          factorId,
        });
        if (challenge.error) throw challenge.error;
        const challengeId = challenge.data?.id;
        if (!challengeId) throw new Error("missing_auth_app_challenge");

        const verification = await supabase.auth.mfa.verify({
          factorId,
          challengeId,
          code: String(code || "").trim(),
        });
        if (verification.error) throw verification.error;
        await supabase.auth.getSession();
        await saveAuthAppEnabled(user.id, authAppEnrollment.label || authAppLabel);
      }
      await new Promise((resolve) => window.setTimeout(resolve, 700));
      await new Promise((resolve) => window.setTimeout(resolve, 900));
      setShowCompletionLoader(false);
      setStep(3);
    } catch (activationError) {
      writeFlowState(flowScope, { step: 2, selectedMethod, sent });
      setShowCompletionLoader(false);
      setStep(2);
      setError(
        selectedMethod === "auth-app"
          ? authAppActivationErrorMessage(t, activationError)
          : t("settings.twoFactor.errors.verifyCode", "We couldn't verify that code.")
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSmsContinue() {
    if (code.length !== 6) {
      setError(t("settings.twoFactor.flow.enterSixDigitCode", "Enter the 6-digit code."));
      return;
    }
    await activateCurrentMethod();
  }

  async function handleAuthAppContinue() {
    if (code.length !== 6) {
      setError(t("settings.twoFactor.flow.enterSixDigitCode", "Enter the 6-digit code."));
      return;
    }
    await activateCurrentMethod();
  }

  async function handleCopyManualKey(event) {
    event?.preventDefault?.();
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(rawManualKey);
      }
      if (document?.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      openSnack(t("settings.twoFactor.flow.setupKeyCopied", "Setup key copied"));
    } catch {
      openSnack(
        t(
          "settings.twoFactor.flow.copyFailed",
          "Copy failed. You can use the manual key above."
        )
      );
    }
  }

  function renderMethodChoice() {
    const welcomeCopy =
      selectedMethod === "auth-app"
        ? t(
            "settings.twoFactor.flow.authAppWelcomeCopy",
            "Once enabled, AfriMed™ will use this app to verify your identity during secure access flows."
          )
        : t(
            "settings.twoFactor.flow.welcomeCopy",
            "Once enabled, AfriMed™ will use this number to verify your identity during secure access flows."
          );

    return (
      <FlowScreen
        footerClassName="pt-4 pb-[calc(72px+env(safe-area-inset-bottom))]"
        footer={
          <div className="flex justify-center">
            <Button
              size="cta"
              fullWidth
              loading={sending || authAppLoading}
              disabled={loadingPhone || !selectedMethodAvailable || authAppLoading}
              onClick={handleContinueStepOne}
            >
              {t("common.continue", "Continue")}
            </Button>
          </div>
        }
      >
        <div className="mx-auto flex w-full max-w-[380px] flex-col items-center text-center">
          <div className="mx-auto mt-[clamp(2.5rem,10vh,5.5rem)] max-w-[326px]">
            <h1 className="text-title-large text-on-surface">
              {t("settings.twoFactor.flow.welcomeBack", "Welcome back!")}
            </h1>
            <p className="mt-2 text-body-large text-on-surface-var">
              {welcomeCopy}
            </p>
          </div>

          <div className="mt-[clamp(4.5rem,16vh,9rem)] w-full">
            <div className="pb-2 text-left text-title-medium text-on-surface-var">
              {t("settings.twoFactor.flow.verifyWith", "Enable two-factor protection with")}
            </div>
            <div className="space-y-6">
              <MethodCard
                icon={ICON_SMS}
                label={t("settings.twoFactor.flow.smsLabel", "SMS")}
                value={phoneDisplay}
                active={selectedMethod === "sms"}
                disabled={!smsAvailable}
                onClick={() => setSelectedMethod("sms")}
              />
              <MethodCard
                icon={ICON_GOOGLE}
                label={t("settings.twoFactor.flow.googleAuthenticator", "Google Authenticator")}
                value={
                  authAppAvailable
                    ? t("settings.twoFactor.flow.authAppValue", "You'll find the code in the app")
                    : t("settings.twoFactor.flow.comingSoon", "Coming soon")
                }
                active={selectedMethod === "auth-app"}
                disabled={!authAppAvailable}
                onClick={() => setSelectedMethod("auth-app")}
              />
            </div>
          </div>

          <button
            type="button"
            className="mt-[clamp(2.75rem,8vh,4.25rem)] w-full text-center text-body-large text-primary"
            disabled={authAppLoading || submitting || showCompletionLoader}
            onClick={handleRegisterNewDevice}
          >
            {t("settings.twoFactor.flow.registerNewDevice", "Register new device")}
          </button>

          {error ? (
            <div className="mt-6 w-full px-2 text-center text-body-medium text-[rgb(var(--schemes-critical))]">
              <p>{error}</p>
              {unfinishedAuthAppFactorId ? (
                <div className="mt-3">
                  <p className="text-body-small">
                    {t(
                      "settings.twoFactor.flow.authAppStartOverCopy",
                      "This will discard the unfinished authenticator setup and create a new setup key."
                    )}
                  </p>
                  <SaveChip
                    type="button"
                    className="mt-3 bg-[var(--state-critical-08)] text-[rgb(var(--schemes-critical))] hover:bg-[var(--state-critical-12)] focus-visible:ring-[rgba(var(--schemes-critical),0.34)]"
                    disabled={authAppLoading || submitting || showCompletionLoader}
                    onClick={handleStartOverAuthAppSetup}
                  >
                    {t("settings.twoFactor.flow.authAppStartOver", "Start over")}
                  </SaveChip>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </FlowScreen>
    );
  }

  function renderSmsStep() {
    return (
      <FlowScreen contentClassName="items-center justify-center pt-0">
        <div className="mx-auto flex w-full max-w-[420px] flex-col items-center">
          <div className="inline-flex flex-col items-start">
            <div className="text-left text-title-medium text-on-surface-var">
              {t("settings.twoFactor.sms.codeLabel", "6-digit code")}
            </div>

            <div className="mt-4">
              <InputOtp
                value={code}
                onChange={setCode}
                length={6}
                align="start"
                tone="surface"
                autoFocus
              />
            </div>
          </div>

          <p className="mt-7 max-w-[380px] text-center text-body-large text-on-surface-var">
            {t("settings.twoFactor.flow.sentCodePrefix", "We've sent a verification code to")}{" "}
            <span className="text-on-surface">{phoneDisplay}</span>.{" "}
            {t("settings.twoFactor.flow.checkMessagingApp", "Please check your messaging app.")}
          </p>

          <div className="mt-[clamp(3rem,8vh,4.5rem)] w-full max-w-[380px] text-center text-body-large text-on-surface-var">
            {countdown > 0 ? (
              <>
                {t("settings.twoFactor.flow.resendCodeIn", "Resend code in")}{" "}
                <span className="text-on-surface">0:{String(countdown).padStart(2, "0")}</span>
              </>
            ) : (
              <button
                type="button"
                className="text-primary"
                disabled={sending}
                onClick={async () => {
                  const ok = await handleSendSmsCode();
                  if (ok) {
                    openSnack(t("settings.twoFactor.flow.sentNewCode", "We've sent a new code."));
                  }
                }}
              >
                {t("settings.twoFactor.sms.resendCode", "Resend code")}
              </button>
            )}
          </div>

          {error ? (
            <div className="mt-6 w-full max-w-[380px] afrimed-card-radius bg-[var(--state-critical-08)] px-4 py-3 text-center text-body-medium text-[rgb(var(--schemes-critical))]">
              {error}
            </div>
          ) : null}

          <div className="mt-[clamp(2.5rem,8vh,4rem)] w-full max-w-[380px]">
            <Button
              size="cta"
              fullWidth
              loading={submitting}
              disabled={code.length !== 6}
              onClick={handleSmsContinue}
            >
              {t("common.continue", "Continue")}
            </Button>
          </div>
        </div>
      </FlowScreen>
    );
  }

  function renderAuthAppStep() {
    return (
      <FlowScreen
        contentClassName="md:items-center md:justify-center md:pt-0"
        footerClassName="pt-5 pb-[calc(72px+env(safe-area-inset-bottom))] md:pt-10 md:pb-0"
        footer={
          <div className="space-y-4">
            <div className="flex justify-center">
              <Button
                size="cta"
                fullWidth
                loading={submitting}
                disabled={code.length !== 6 || !authAppEnrollment?.factorId}
                onClick={handleAuthAppContinue}
              >
                {t("common.continue", "Continue")}
              </Button>
            </div>
            <div className="flex justify-center">
              <Button size="cta" variant="ghost" fullWidth disabled={submitting} onClick={handleBack}>
                {t("common.back", "Back")}
              </Button>
            </div>
          </div>
        }
      >
        <div className="mx-auto flex w-full max-w-[380px] flex-col items-center text-center md:max-w-[460px]">
          <div className="mx-auto max-w-[348px] text-title-large text-on-surface md:max-w-none">
            {t("settings.twoFactor.flow.setUpGoogleAuthenticator", "Set Up Google Authenticator")}
          </div>

          <div className="mt-10 flex justify-center">
            {authAppQrSrc ? (
              <Card className="p-4">
                <img
                  src={authAppQrSrc}
                  alt={t("settings.twoFactor.flow.authAppQrAlt", "Authenticator app setup QR code")}
                  className="h-[160px] w-[160px] rounded-[8px] bg-white"
                  draggable="false"
                />
              </Card>
            ) : null}
          </div>

          <div className="mx-auto mt-[clamp(3rem,10vh,4.75rem)] max-w-[348px] md:max-w-[430px]">
            <div className="text-title-large text-on-surface">
              {t("settings.twoFactor.flow.scanQrCode", "Scan QR code")}
            </div>
            <p className="mt-2 text-[15px] leading-6 text-on-surface-var md:text-body-large">
              {t(
                "settings.twoFactor.flow.scanQrCopy",
                "Scan the QR code above with an authenticator app on your separate device, or tap \"Can't scan QR code\" to reveal the manual key below."
              )}
            </p>
          </div>

          <button
            type="button"
            className="mt-[clamp(2.75rem,8vh,3.75rem)] text-body-large text-primary"
            onClick={() => setShowManualKey((current) => !current)}
          >
            {t("settings.twoFactor.flow.cantScanQrCode", "Can't scan QR code")}
          </button>

          {showManualKey ? (
            <Card className="mt-4 w-full px-4 py-4 text-left">
              <div className="text-label-medium text-on-surface-var">
                {t("settings.twoFactor.flow.setupKey", "Setup key")}
              </div>
              <div className="mt-1 flex items-center gap-3">
                <div className="min-w-0 flex-1 break-all font-mono text-body-large text-on-surface">
                  {rawManualKey || manualKey}
                </div>
                <button
                  type="button"
                  className="text-body-medium text-primary"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={handleCopyManualKey}
                >
                  {t("settings.twoFactor.flow.copy", "Copy")}
                </button>
              </div>
              <p className="mt-3 text-body-medium text-on-surface-var">
                {t(
                  "settings.twoFactor.flow.manualKeyNoSpaces",
                  "Copy and paste this key without spaces if your authenticator app asks for a setup key."
                )}
              </p>
              <div className="mt-4 grid grid-cols-2 gap-x-4 gap-y-2 text-body-small">
                <div>
                  <div className="text-label-medium text-on-surface-var">
                    {t("settings.twoFactor.flow.issuer", "Issuer")}
                  </div>
                  <div className="mt-0.5 break-words text-on-surface">
                    {formatVisibleIssuer(manualSetupDetails.issuer)}
                  </div>
                </div>
                <div>
                  <div className="text-label-medium text-on-surface-var">
                    {t("settings.twoFactor.flow.account", "Account")}
                  </div>
                  <div className="mt-0.5 break-words text-on-surface">
                    {manualSetupDetails.account || user?.email || "AfriMed"}
                  </div>
                </div>
              </div>
            </Card>
          ) : null}

          <div className="mt-7 inline-flex flex-col items-start">
            <div className="text-left text-title-medium text-on-surface-var">
              {t("settings.twoFactor.sms.codeLabel", "6-digit code")}
            </div>

            <div className="mt-4">
              <InputOtp
                value={code}
                onChange={setCode}
                length={6}
                align="start"
                tone="surface"
                autoFocus
              />
            </div>
          </div>

          {error ? (
            <div className="mt-6 w-full afrimed-card-radius bg-[var(--state-critical-08)] px-4 py-3 text-left text-body-medium text-[rgb(var(--schemes-critical))]">
              {error}
            </div>
          ) : null}
        </div>
      </FlowScreen>
    );
  }

  function renderSuccessStep() {
    return (
      <FlowScreen
        contentClassName="justify-start pt-[clamp(3rem,11vh,6rem)] md:items-center md:justify-center md:pt-0"
        footerClassName="pt-8 pb-[calc(24px+env(safe-area-inset-bottom))] md:pt-10 md:pb-0"
        footer={
          <div className="flex justify-center">
            <Button
              size="cta"
              fullWidth
              onClick={() => {
                clearFlowState(flowScope);
                onDone?.();
              }}
            >
              {t("common.continue", "Continue")}
            </Button>
          </div>
        }
      >
        <div className="mx-auto flex w-full max-w-[358px] flex-col items-center justify-center px-0 text-center md:max-w-[520px]">
          <SuccessBadge />
          <div className="mt-[45px] w-full md:mt-12">
            <div className="text-headline-small text-on-surface">
              {t("settings.twoFactor.flow.successTitle", "You're in!")}
            </div>
            <p className="mx-auto mt-2 max-w-[358px] text-body-large text-on-surface md:max-w-[520px]">
              {t(
                "settings.twoFactor.flow.successCopy",
                "You've successfully secured your AfriMed™ account."
              )}
            </p>
          </div>
        </div>
      </FlowScreen>
    );
  }

  function renderCompletionLoader() {
    return (
      <FlowScreen contentClassName="items-center justify-center pt-[clamp(4rem,16vh,8rem)] md:pt-0">
        <div className="mx-auto flex w-full max-w-[358px] flex-col items-center justify-center text-center">
          <CircularProgressIndicator
            progress={completionProgress}
            label={t("common.loading", "Loading...")}
          />
        </div>
      </FlowScreen>
    );
  }

  return (
    <div className="w-full" data-hide-tabbar="true">
      <div className="afrimed-rail afrimed-rail--comfortable pt-4 pb-[calc(32px+env(safe-area-inset-bottom))] md:pb-24">
        <FlowHeader
          step={showCompletionLoader ? 3 : step}
          onBack={handleBack}
          backLabel={t("common.back", "Back")}
        />

        {showCompletionLoader ? renderCompletionLoader() : null}
        {!showCompletionLoader && step === 3 ? renderSuccessStep() : null}
        {!showCompletionLoader && step === 1 ? renderMethodChoice() : null}
        {!showCompletionLoader && step === 2 && selectedMethod === "sms" ? renderSmsStep() : null}
        {!showCompletionLoader && step === 2 && selectedMethod === "auth-app" ? renderAuthAppStep() : null}

        <Snackbar open={!!snackbar} onClose={() => setSnackbar("")}>
          {snackbar}
        </Snackbar>
      </div>
    </div>
  );
}
